import { TIME_SPENT_BY_ACTIVITY_TYPE_KEYS } from '@web/config';
import { getTooltipPeriodReferenceYear, normalizePeriodComparisonLabel } from '@web/utility/ tooltip/periodComparisonTooltip';
import { secondsToHours } from '@web/utility/date';
import { sumTimeSpentByActivityTypePeriodTotal, sumTimeSpentOnWorkActivitiesByEntityPeriodTotal } from '@workalizer/shared/services/processTimeSpentOnWorkActivities';
import { diffComparisonType, diffComparisonValueFormat } from '@workalizer/shared/types';
import { createPeriodComparisonMatcherFromItems, getPercentageDiff, getPreviousPeriod } from '@workalizer/shared/utils';
export function getTimeSpentByActivityTypeValue(period, key) {
    var _a, _b, _c, _d, _e, _f;
    switch (key) {
        case 'communication':
            return ((_a = period.CHAT) !== null && _a !== void 0 ? _a : 0) + ((_b = period.GMAIL) !== null && _b !== void 0 ? _b : 0);
        case 'meetings':
            return (_c = period.MEET) !== null && _c !== void 0 ? _c : 0;
        case 'scheduledTime':
            return (_d = period.CALENDAR) !== null && _d !== void 0 ? _d : 0;
        case 'documentWork':
            return (_e = period.DRIVE) !== null && _e !== void 0 ? _e : 0;
        case 'aiActivity':
            return (_f = period.GEMINI) !== null && _f !== void 0 ? _f : 0;
    }
}
export function sumTimeSpentByActivityTypePeriods(periods, key) {
    return periods.reduce(function (sum, period) { return sum + getTimeSpentByActivityTypeValue(period, key); }, 0);
}
export function buildTimeSpentByActivityTypeLegendItems(currentPeriods, previousPeriods, teamAverageByType) {
    return TIME_SPENT_BY_ACTIVITY_TYPE_KEYS.map(function (key) {
        var currentTotal = sumTimeSpentByActivityTypePeriods(currentPeriods, key);
        var previousTotal = sumTimeSpentByActivityTypePeriods(previousPeriods, key);
        var teamAverageTotal = teamAverageByType
            ? sumTimeSpentByActivityTypePeriods(teamAverageByType, key)
            : undefined;
        return {
            key: key,
            currentTotal: currentTotal,
            diffValue: getPercentageDiff(previousTotal, currentTotal, {
                comparisonValueFormat: diffComparisonValueFormat.durationSeconds
            }),
            teamAverageDiffValue: teamAverageTotal != null
                ? getPercentageDiff(teamAverageTotal, currentTotal, {
                    comparisonValueFormat: diffComparisonValueFormat.durationSeconds,
                    comparisonType: diffComparisonType.teamAverage
                })
                : undefined
        };
    });
}
export function buildTimeSpentByActivityTypeChartData(currentPeriods, previousPeriods, options, valueFormatter) {
    var _a;
    if (valueFormatter === void 0) { valueFormatter = secondsToHours; }
    var referenceYear = getTooltipPeriodReferenceYear(options.dateFrom, options.dateTo);
    var previousRange = getPreviousPeriod(options.dateFrom, options.dateTo);
    var periodMatcher = createPeriodComparisonMatcherFromItems(currentPeriods, previousPeriods, options);
    var teamByPeriodName = new Map(((_a = options.teamAverageByType) !== null && _a !== void 0 ? _a : []).map(function (period) { return [period.name, period]; }));
    return currentPeriods.map(function (period, index) {
        var previousPeriod = periodMatcher.getItem(index, previousPeriods);
        var teamPeriod = teamByPeriodName.get(period.name);
        var currentPeriodLabel = normalizePeriodComparisonLabel(period.name, options.dateFrom, options.dateTo, referenceYear);
        var previousPeriodLabel = (previousPeriod === null || previousPeriod === void 0 ? void 0 : previousPeriod.name)
            ? normalizePeriodComparisonLabel(previousPeriod.name, previousRange.prevPeriodDateFrom, previousRange.prevPeriodDateTo, referenceYear)
            : '';
        var row = {
            period: period.name,
            currentPeriodComparisonLabel: currentPeriodLabel,
            previousPeriodLabel: previousPeriodLabel
        };
        TIME_SPENT_BY_ACTIVITY_TYPE_KEYS.forEach(function (key) {
            var dataKeySuffix = key.charAt(0).toUpperCase() + key.slice(1);
            row["current".concat(dataKeySuffix)] = valueFormatter(getTimeSpentByActivityTypeValue(period, key));
            row["previous".concat(dataKeySuffix)] = previousPeriod
                ? valueFormatter(getTimeSpentByActivityTypeValue(previousPeriod, key))
                : 0;
            if (options.teamAverageByType) {
                row["team".concat(dataKeySuffix)] = teamPeriod
                    ? valueFormatter(getTimeSpentByActivityTypeValue(teamPeriod, key))
                    : 0;
            }
        });
        return row;
    });
}
export function buildTimeSpentOnWorkActivitiesEntityLegendItems(currentByEntity, previousByEntity) {
    var sumTotals = function (byEntity) {
        return byEntity.reduce(function (totals, entity) {
            totals[entity.id] = entity.activities.reduce(function (sum, activity) { return sum + activity.duration; }, 0);
            return totals;
        }, {});
    };
    var currentTotals = sumTotals(currentByEntity);
    var previousTotals = sumTotals(previousByEntity);
    return currentByEntity.map(function (entity) {
        var _a, _b, _c;
        return ({
            id: entity.id,
            title: entity.title,
            currentTotal: (_a = currentTotals[entity.id]) !== null && _a !== void 0 ? _a : 0,
            diffValue: getPercentageDiff((_b = previousTotals[entity.id]) !== null && _b !== void 0 ? _b : 0, (_c = currentTotals[entity.id]) !== null && _c !== void 0 ? _c : 0, { comparisonValueFormat: diffComparisonValueFormat.durationSeconds })
        });
    });
}
var entityStackDataKey = function (prefix, entityId) {
    return "".concat(prefix, "_").concat(entityId);
};
var getEntityDurationForPeriod = function (byEntity, entityId, periodDate) {
    var _a;
    var entity = byEntity.find(function (group) { return group.id === entityId; });
    var activity = entity === null || entity === void 0 ? void 0 : entity.activities.find(function (item) { return item.date === periodDate; });
    return (_a = activity === null || activity === void 0 ? void 0 : activity.duration) !== null && _a !== void 0 ? _a : 0;
};
export function buildTimeSpentOnWorkActivitiesEntityChartSegments(currentByEntity) {
    return currentByEntity.map(function (entity) { return ({
        id: entity.id,
        title: entity.title,
        previousKey: entityStackDataKey('previous', entity.id),
        currentKey: entityStackDataKey('current', entity.id)
    }); });
}
export function buildTimeSpentOnWorkActivitiesEntityChartData(currentPeriods, previousPeriods, currentByEntity, previousByEntity, options, valueFormatter) {
    if (valueFormatter === void 0) { valueFormatter = secondsToHours; }
    var referenceYear = getTooltipPeriodReferenceYear(options.dateFrom, options.dateTo);
    var previousRange = getPreviousPeriod(options.dateFrom, options.dateTo);
    var periodMatcher = createPeriodComparisonMatcherFromItems(currentPeriods, previousPeriods, options);
    var segments = buildTimeSpentOnWorkActivitiesEntityChartSegments(currentByEntity);
    return currentPeriods.map(function (period, index) {
        var previousPeriod = periodMatcher.getItem(index, previousPeriods);
        var currentPeriodLabel = normalizePeriodComparisonLabel(period.name, options.dateFrom, options.dateTo, referenceYear);
        var previousPeriodLabel = (previousPeriod === null || previousPeriod === void 0 ? void 0 : previousPeriod.name)
            ? normalizePeriodComparisonLabel(previousPeriod.name, previousRange.prevPeriodDateFrom, previousRange.prevPeriodDateTo, referenceYear)
            : '';
        var row = {
            period: period.name,
            currentPeriodComparisonLabel: currentPeriodLabel,
            previousPeriodLabel: previousPeriodLabel
        };
        segments.forEach(function (segment) {
            var _a;
            row[segment.previousKey] = valueFormatter(getEntityDurationForPeriod(previousByEntity, segment.id, (_a = previousPeriod === null || previousPeriod === void 0 ? void 0 : previousPeriod.name) !== null && _a !== void 0 ? _a : ''));
            row[segment.currentKey] = valueFormatter(getEntityDurationForPeriod(currentByEntity, segment.id, period.name));
        });
        return row;
    });
}
var periodTotalSeconds = function (period, byEntity, periodDate) {
    var byTypeTotal = period
        ? sumTimeSpentByActivityTypePeriodTotal(period)
        : 0;
    var byEntityTotal = sumTimeSpentOnWorkActivitiesByEntityPeriodTotal(byEntity, periodDate);
    // Prefer whichever series actually has activity for the period.
    return Math.max(byTypeTotal, byEntityTotal);
};
export function buildTimeSpentOnWorkActivitiesUserChartData(currentPeriods, previousPeriods, currentByEntity, previousByEntity, teamAverage, options, valueFormatter) {
    if (valueFormatter === void 0) { valueFormatter = secondsToHours; }
    var referenceYear = getTooltipPeriodReferenceYear(options.dateFrom, options.dateTo);
    var previousRange = getPreviousPeriod(options.dateFrom, options.dateTo);
    var periodMatcher = createPeriodComparisonMatcherFromItems(currentPeriods, previousPeriods, options);
    var teamByDate = new Map((teamAverage !== null && teamAverage !== void 0 ? teamAverage : []).map(function (period) { return [period.date, period.duration]; }));
    return currentPeriods.map(function (period, index) {
        var _a, _b;
        var previousPeriod = periodMatcher.getItem(index, previousPeriods);
        var currentPeriodLabel = normalizePeriodComparisonLabel(period.name, options.dateFrom, options.dateTo, referenceYear);
        var previousPeriodLabel = (previousPeriod === null || previousPeriod === void 0 ? void 0 : previousPeriod.name)
            ? normalizePeriodComparisonLabel(previousPeriod.name, previousRange.prevPeriodDateFrom, previousRange.prevPeriodDateTo, referenceYear)
            : '';
        return {
            period: period.name,
            currentPeriodComparisonLabel: currentPeriodLabel,
            previousPeriodLabel: previousPeriodLabel,
            previous: valueFormatter(periodTotalSeconds(previousPeriod, previousByEntity, (_a = previousPeriod === null || previousPeriod === void 0 ? void 0 : previousPeriod.name) !== null && _a !== void 0 ? _a : '')),
            current: valueFormatter(periodTotalSeconds(period, currentByEntity, period.name)),
            teamAverage: valueFormatter((_b = teamByDate.get(period.name)) !== null && _b !== void 0 ? _b : 0)
        };
    });
}
export function buildTimeSpentOnWorkActivitiesUserLegendTotals(currentPeriods, previousPeriods, currentByEntity, previousByEntity, teamAverage) {
    var currentTotal = currentPeriods.reduce(function (sum, period) {
        return sum + periodTotalSeconds(period, currentByEntity, period.name);
    }, 0);
    var previousTotal = previousPeriods.reduce(function (sum, period) {
        return sum + periodTotalSeconds(period, previousByEntity, period.name);
    }, 0);
    var teamAverageTotal = (teamAverage !== null && teamAverage !== void 0 ? teamAverage : []).reduce(function (sum, period) { return sum + period.duration; }, 0);
    var teamAverageDiffValue = teamAverage != null && teamAverage.length > 0
        ? getPercentageDiff(teamAverageTotal, currentTotal, {
            comparisonValueFormat: diffComparisonValueFormat.durationSeconds,
            comparisonType: diffComparisonType.teamAverage
        })
        : undefined;
    return {
        previousTotal: previousTotal,
        currentTotal: currentTotal,
        teamAverageTotal: teamAverageTotal,
        diffValue: getPercentageDiff(previousTotal, currentTotal, {
            comparisonValueFormat: diffComparisonValueFormat.durationSeconds
        }),
        teamAverageDiffValue: teamAverageDiffValue
    };
}
